import { FieldHelperText, type FieldProps } from "@firecms/core";
import { TextField } from "@firecms/ui";

/**
 * A multi-line string field that does not break IME composition.
 *
 * **Use this for every `multiline: true` string property. Never ship the stock
 * one.** FireCMS's `TextFieldBinding` sends a multiline string to
 * `TextareaAutosize` (a single-line one goes to a plain `<input>`, which is why
 * `title` was never affected), and that component measures its own height in a
 * `useLayoutEffect` **with no dependency array** — so it runs after every
 * render, and the measurement writes to the DOM node directly:
 *
 * ```js
 * const prevValue = el.value;
 * el.value = "x";                       // measure one row
 * const singleRowScrollH = el.scrollHeight;
 * el.value = prevValue;                 // put it back
 * ```
 *
 * The form is controlled, so every keystroke re-renders, so that write lands
 * **mid-composition**. Assigning to `el.value` cancels the browser's active
 * composition session, and the IME's pending buffer is committed as whatever
 * raw characters it held: typing 注音 in Zhuyin produced ㄓㄨˋㄧㄣ. Every
 * composition-based input method is affected — Zhuyin, Pinyin, Japanese kana,
 * Korean — and the damage is invisible in English, where each keystroke is
 * already a finished character.
 *
 * `TextField multiline` from `@firecms/ui` renders a plain `<textarea rows>`
 * with React's own controlled value and no imperative writes, so composition
 * survives. The trade is a fixed height instead of a growing one, which for a
 * meta description — two or three lines by design — is not a real loss.
 *
 * Fixed here rather than by patching `@firecms/ui`: a patch would cover every
 * multiline field at once, including any FireCMS adds later, but it has to be
 * re-verified on every upgrade, and this repo has exactly one such field. If a
 * third or fourth appears, `pnpm patch @firecms/ui` guarding that write with
 * `compositionstart`/`compositionend` is the better trade.
 */
export function PlainTextAreaField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    isSubmitting,
    includeDescription,
    autoFocus,
}: FieldProps<string>) {
    return (
        <>
            <TextField
                // `?? ""` keeps this a controlled field across a null the store
                // may hold: React would otherwise switch it to uncontrolled and
                // warn, and the value would stop tracking the form.
                value={value ?? ""}
                // The stock binding's own behaviour — an emptied field stores
                // `""`, not null. `blankableLocalizedText` in
                // `packages/shared/cms/schema.ts` reads a pair of those as "no
                // description at all".
                onChange={(event) => setValue(event.target.value)}
                label={property.name}
                multiline
                minRows={3}
                error={showError}
                disabled={disabled || isSubmitting}
                autoFocus={autoFocus}
            />

            <FieldHelperText
                includeDescription={includeDescription}
                showError={showError}
                error={error}
                property={property}
            />
        </>
    );
}
